// Port of prototype/components/script/VersionRow.jsx (design D3/D11): same
// name, same prop contract (prototype/components/script/VersionRow.d.ts),
// styled only from token CSS vars. UI.md §4: the version bar's stepper row
// ("← / → step through script history"); renders exactly one pip at a time — a
// real version, or the session's failed-generation pseudo-pip (`version={null}`)
// — the parent (task group 8's `Colony.tsx`) owns stepping between them and
// closes over the currently-focused pip's own version number when wiring
// `onActivate` to a `Rollback{targetVersion}` dispatch (design D6; see this
// component's `.prompt.md`: `onActivate={() => rollback(3)}`) — this component
// itself carries no dispatch opinion, matching `version-model.ts`.
//
// Fix vs. the kit source (task 7.2, same fix pattern as task 6.2's `Sheet`): the
// kit's nav buttons are a fixed 32x32px square and its Activate button a 34px
// min-height, both below the design system's normative ≥44px hit target (UI.md
// §2). Ported at 44px in both dimensions here and mirrored back into
// `prototype/components/script/VersionRow.jsx` in the same change (the
// prototype/CLAUDE.md sync contract). `prototype/ui_kits/waggle-app/colony.html`'s
// own bespoke `.revnav`/`.revact` CSS (a separate composition, like the kit
// Sheet's real-usage close button) is unchanged here — flagged for task group 8's
// `Colony.tsx` scope, which composes the real sheet header/version bar.

import type { CSSProperties } from 'react';

export interface VersionRowProps {
  /** The version pip shown, or `null` to render the session's failed-generation
   * pseudo-pip instead (a failed generation carries no version number — design D6). */
  version: number | null;
  /** One-line title for the row. */
  summary: string;
  /** Provenance/status meta line, e.g. 'Prompted 2 min ago · auto-repaired ×1',
   * 'No details — activate to view' (a bare, pre-connect version), or a failed
   * generation's diagnostics summary. */
  meta?: string;
  /** This is the active (hot-swapped-in) version — shows the "Live" badge, no Activate. */
  isLive?: boolean;
  /** Rendering the failed pseudo-pip (red ✕) — never activatable. */
  failed?: boolean;
  /** Nav-arrow availability; the bar always renders both, disabled at the ends
   * of the session-scoped pip range (design D6). */
  canStepOlder?: boolean;
  canStepNewer?: boolean;
  onStepOlder?: () => void;
  onStepNewer?: () => void;
  /** One-tap rollback (`Rollback{targetVersion: version}`); omitted/undefined
   * for the live pip and the failed pseudo-pip — neither is activatable. The
   * caller closes over `version` itself (this component has no dispatch opinion). */
  onActivate?: () => void;
  style?: CSSProperties;
}

const HIT_TARGET = 44;

const navBtnStyle: CSSProperties = {
  width: HIT_TARGET,
  height: HIT_TARGET,
  flex: 'none',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line-strong)',
  background: 'var(--paper-1)',
  color: 'var(--ink-2)',
  fontSize: 15,
  lineHeight: 1,
  cursor: 'pointer',
};

export function VersionRow({
  version,
  summary,
  meta,
  isLive,
  failed,
  canStepOlder = true,
  canStepNewer = true,
  onStepOlder,
  onStepNewer,
  onActivate,
  style,
}: VersionRowProps) {
  const pipBg = failed ? 'var(--danger-soft)' : isLive ? 'var(--honey-500)' : 'var(--paper-2)';
  const pipColor = failed ? 'var(--danger)' : isLive ? '#FFFDF6' : 'var(--ink-2)';
  const pipLabel = failed ? '✕' : `v${version}`;
  const showActivate = !isLive && !failed && Boolean(onActivate);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--surface-card)',
        border: '1.5px solid var(--line-strong)',
        boxShadow: 'var(--shadow-card)',
        borderRadius: 'var(--r-lg)',
        padding: '6px 8px',
        ...style,
      }}
    >
      <button onClick={onStepOlder} disabled={!canStepOlder} aria-label="Older version" style={navBtnStyle}>
        ←
      </button>
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 13,
          flex: 'none',
          minWidth: 34,
          height: 26,
          padding: '0 6px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 13,
          background: pipBg,
          color: pipColor,
        }}
      >
        {pipLabel}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontWeight: 700,
            fontSize: 13,
            color: 'var(--ink-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary}
        </div>
        {meta && (
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)' }}>{meta}</div>
        )}
      </div>
      <button onClick={onStepNewer} disabled={!canStepNewer} aria-label="Newer version" style={navBtnStyle}>
        →
      </button>
      {isLive && (
        <span
          style={{
            flex: 'none',
            fontFamily: 'var(--font-body)',
            fontWeight: 800,
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--accent-strong)',
          }}
        >
          Live
        </span>
      )}
      {showActivate && (
        <button
          onClick={onActivate}
          style={{
            flex: 'none',
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 13,
            minHeight: HIT_TARGET,
            padding: '0 14px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--accent-fill)',
            color: '#FFFDF6',
            border: '1px solid var(--accent-strong)',
            cursor: 'pointer',
          }}
        >
          Activate
        </button>
      )}
    </div>
  );
}
